import React from "react";
import {Box, IconButton, Stack, Typography} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import type {
    MarketingHealthV4,
    MarketingInterestAudienceV4,
    MarketingLifecycleV4,
} from "../models/marketing-v4";
import {MarketingInsightsV4Service} from "../services/marketing-insights-v4.service";
import LifecycleAudiencesSection from "../v2/marketing-overview/LifecycleAudiencesSection";
import InterestAudiencesSection from "../v2/marketing-overview/InterestAudiencesSection";
import MarketingGuestBaseCard from "../v2/marketing-overview/MarketingGuestBaseCard";
import MarketingHealthStatCards from "../v2/marketing-overview/MarketingHealthStatCards";
import {InsightsTooltip} from "../kit/InsightsTooltip";
import {insightsLoadingOf} from "../kit/insightsLoading";

/** A section label and its hint, in the same ⓘ every card on every insights page uses. */
function SectionHeader({title, hint}: {title: string; hint?: string}) {
    return (
        <Stack direction={"row"} alignItems={"center"} gap={0.25} sx={{minWidth: 0}}>
            <Typography variant={"body1"} color={"text.secondary"}>{title}</Typography>

            {hint && (
                <InsightsTooltip placement={"top"} title={hint}>
                    <IconButton
                        size={"small"}
                        aria-label={`${title} information`}
                        sx={{color: "text.disabled", flexShrink: 0, p: 0.5}}
                    >
                        <InfoOutlinedIcon sx={{fontSize: 18}}/>
                    </IconButton>
                </InsightsTooltip>
            )}
        </Stack>
    );
}

interface MarketingOverviewTabProps {
    /** The rail's search. A section whose content is filtered out drops its heading with it. */
    matches?: (title: string, key: string) => boolean;
}

export function MarketingOverviewTab({matches}: MarketingOverviewTabProps) {
    const {t} = useTranslation();
    const currency = useOrganization()?.currencyCode || "EUR";

    const healthQuery = useQuery<MarketingHealthV4>({
        queryKey: ["marketing-insights-v4-health"],
        queryFn: MarketingInsightsV4Service.health,
        staleTime: 60_000,
    });

    const interestsQuery = useQuery<MarketingInterestAudienceV4[]>({
        queryKey: ["marketing-insights-v4-interests"],
        queryFn: MarketingInsightsV4Service.interests,
        staleTime: 60_000,
    });

    const audiencesQuery = useQuery<MarketingLifecycleV4>({
        queryKey: ["marketing-insights-v4-audiences"],
        queryFn: MarketingInsightsV4Service.audiences,
        staleTime: 60_000,
    });

    const shows = (title: string) => matches == null || matches(t(title), title);
    const showGuestBase = shows("Guest base");
    const showLifecycle = shows("Lifecycle audiences");
    const showInterests = shows("Interest audiences");
    const showHealth = shows("Marketing reach & performance");

    if (!showGuestBase && !showLifecycle && !showInterests && !showHealth) {
        return (
            <Typography variant={"body2"} color={"text.secondary"} sx={{py: 6, textAlign: "center"}}>
                {t("No insights match your search.")}
            </Typography>
        );
    }

    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: 3}}>
            {showGuestBase && (
                <MarketingGuestBaseCard
                    segments={audiencesQuery.data?.segments}
                    loading={insightsLoadingOf(audiencesQuery.isLoading)}
                />
            )}

            {showLifecycle && (
            <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
                <SectionHeader
                    title={t("Lifecycle audiences")}
                    hint={t("Ready-made audiences for your next campaign, each targeting a lifecycle moment worth acting on.")}
                />

                <LifecycleAudiencesSection
                    data={audiencesQuery.data}
                    loading={audiencesQuery.isLoading}
                    currency={currency}
                />
            </Box>
            )}

            {showInterests && (
            <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
                <SectionHeader
                    title={t("Interest audiences")}
                    hint={t("Every guest interest, ready to target — size, how many you can email, and one click to build the audience.")}
                />

                <InterestAudiencesSection
                    cards={interestsQuery.data}
                    loading={interestsQuery.isLoading}
                />
            </Box>
            )}

            {showHealth && (
            <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
                <SectionHeader
                    title={t("Marketing reach & performance")}
                    hint={t("The usable audience, list health, and the synchronized newsletter activity needed to judge performance.")}
                />

                <MarketingHealthStatCards
                    health={healthQuery.data}
                    loading={insightsLoadingOf(healthQuery.isLoading)}
                />
            </Box>
            )}
        </Box>
    );
}

export default MarketingOverviewTab;
